<template>
  <div class="list">
    <div class="list-table" :class="{'list-table-split': sliderShown}">
      <div class="list-header">
        <TextInput
          v-model="search"
          name="search"
          label="Search name, email, policy number, status, etc."
          type="text"
          @input="handleQuerychange"
        />
      </div>
      <data-tables-server 
      :data="list" 
      :loading="loading"  
      :empty-text="'No data'" 
      :total="total"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-page-change="handleCurrentPageChange"
      @row-click="rowClick"
      @size-change="handleSizeChange"
      :pagination-props="{ background: true, pageSizes: [5, 25, 50, 100] }"
      :table-props="{
        border: true,
        stripe: true,
        rowClassName: tableRowClassName
      }">
        <el-table-column
          :index="0"
          show-overflow-tooltip
          :autoFit='true'
          label="Name">
          <template slot-scope="scope">
            {{ scope.row.equipment_owner.name }} 
            <p>({{ scope.row.equipment_owner.email }})</p>
          </template>
        </el-table-column>

        <el-table-column
          :index="1"
          label="Mailing Address">
          <template slot-scope="scope">
            {{ scope.row.equipment_owner.address }}
          </template>
        </el-table-column>

        <el-table-column
          :index="2"
          label="Properties">
          <template slot-scope="scope">
            <p v-for="(eq, index) in scope.row.equipment" :key="index">
              {{ eq.description }} ({{ propValue(eq) }})
            </p>
          </template>
        </el-table-column>

        <el-table-column
          v-if="!sliderShown"
          :index="3"
          :width="90"
          label="Premium">
          <template slot-scope="scope">
            ${{ annualPremium(scope.row) }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="!sliderShown"
          :index="4"
          :width="120"
          label="Purchase Date">
          <template slot-scope="scope">
            {{ scope.row.created_at  }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="!sliderShown"
          :index="5"
          :width="125"
          label="Policy Effective">
          <template slot-scope="scope">
            {{ scope.row.policy_effective_date }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="!sliderShown"
          :index="6"
          label="Reference #">
          <template slot-scope="scope">
            {{ scope.row.reference_number }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="!sliderShown"
          :index="7"
          label="Policy Number">
          <template slot-scope="scope">
            {{ scope.row.policy_number }}
          </template>
        </el-table-column>

        <el-table-column
          :index="8"
          label="Status">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status_text" :type="type(scope.row.status_text)">{{ scope.row.status_text }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column
          :index="9"
          :width="90"
          label="Actions">
          <template slot-scope="scope">
            <div class="actions">
              <el-tooltip content="Download receipt" placement="top">
                <a class="export" @click="downloadReceipt(scope.row)">
                  <font-awesome-icon :icon="['fal', 'file-download']" />
                </a>
              </el-tooltip>  
              <el-tooltip 
                content="Close duplicate underwriting" 
                placement="top"
                :disabled="closed(scope.row.status_text)" >
                <a 
                  class="close" 
                  :class="{'disabled': closed(scope.row.status_text)}" 
                  @click="closeDuplicate(scope.row)">
                  <font-awesome-icon :icon="['fal', 'trash']" />
                </a>
              </el-tooltip>  
            </div>  
          </template>
        </el-table-column>
      </data-tables-server>
    </div>  

    <transition name="el-zoom-in-top">
      <div v-show="sliderShown" class="list-slider">
          <Success 
          :dashboard="true"
          :startEdit="startEdit"
          :underwriting="underwriting"
          :save="markComplete"
          @close="close"
          @start-policy-edit="startPolicyEdit"
          @cancel-policy-edit="cancelPolicyEdit" />
      </div>
    </transition>  
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { SET_UNDERWRITINGS, FETCH_UNDERWRITINGS, DOWNLOAD_RECEIPT, UPDATE_UNDERWRITING, UPDATE_PAYMENT } from "../../store/types";
import { TextInput } from "../../style";
import Success from "../Quotes/Success.vue";
import { addCommaToNumber } from "../../utils";
import { STATTII } from "../../utils/constants";
export default {
  name: "Dashboard",
  components: {
    TextInput,
    Success
  },
  computed: {
    ...mapState({
      underWritings: state => state.equipment.underWritings,
      meta: state => state.equipment.underWritingsMeta
    }),
    total() {
      const { count = 0, search_count = 0 } = this.meta;
      return search_count < count && search_count > 0 ? search_count : count;
    },
    tableRowClassName: (app) => ({ row }) => {
      return (app.openRowId && row.id === app.openRowId)
        ? "active-row"
        : "";
    },
  },
  data() {
    return {
      item: null,
      team: null,
      isInvite: false,
      removeDialog: false,
      disabled: false,
      leaving: false,
      search: "",
      list: [],
      loading: false,
      currentPage: 1,
      pageSize: 25,
      query: "",
      sliderShown: false,
      underwriting: null,
      startEdit: false,
      openRowId: null
    }
  },
  created() {
    this.setUnderWritings({
      underWritings: [],
      meta: {}
    });
    this.handleSearch();
  },
  methods: {
    ...mapActions({
      fetchUnderWritings: FETCH_UNDERWRITINGS,
      download: DOWNLOAD_RECEIPT,
      updateUnderwriting: UPDATE_UNDERWRITING,
      updatePayment: UPDATE_PAYMENT
    }),
    ...mapMutations({
      setUnderWritings: SET_UNDERWRITINGS
    }),
    handleCurrentPageChange(page) {
      this.currentPage = page;
      this.handleSearch();
    },
    handleSizeChange(page) {
      this.pageSize = page;
      const lastPage = Math.ceil(this.total / page);
      if (this.currentPage <= lastPage) {
        this.handleSearch();
      }
    },
    handleQuerychange(query) {
      this.query = query;
      this.handleSearch();
    },
    handleSearch() {
      this.loadUnderWritings({
        query: this.query,
        limit: this.pageSize,
        offset: this.pageSize * (this.currentPage - 1)
      });
    },
    loadUnderWritings(payload) {
      this.loading = true;
      this.fetchUnderWritings(payload)
        .then(() => (this.loading = false))
        .catch(() => {});
    },
    downloadReceipt(underWriting) {
      this.download({ 
        id: underWriting.uuid,
        dashboard: true
      })
        .then(res => {
          const { data } = res;
          window.open(data, "_blank");
        })
        .catch(() => {});
    },
    markComplete(payment, number) {
      this.updateUnderwriting({
        id: payment.uuid,
        data: {
          number: number
        }
      })
        .then(() => {
          this.$notify({
            type: "success",
            title: "Success",
            message: "Underwriting policy number updated"
          });
          this.startEdit = false;
        })
        .catch();
    },
    rowClick(row, column) {
      if (column
      && column.index !== undefined
      && column.index !== null
      && column.index !== 9) {
        this.underwriting = row;
        this.sliderShown = true;
        this.openRowId = row.id;
        window.scroll(0, 0);
      }
    },
    close() {
      this.openRowId = null;
      this.sliderShown = false;
    },
    annualPremium(row) {
      return addCommaToNumber((parseFloat(row.premium_amount) + parseFloat(row.tria_premium)).toFixed(2));
    },
    type(status) {
      if (status == "Policy Bound") {
        return "warning";
      }

      if (status == "Policy Issued") {
        return "success";
      }

      if (status == "Closed - Duplicate") {
        return "danger";
      }

      return "";
    },
    closed(status) {
      return status == "Closed - Duplicate";
    },
    propValue(eq) {
      return "$" + addCommaToNumber(eq.value);
    },
    startPolicyEdit() {
      this.startEdit = true;
    },
    cancelPolicyEdit() {
      this.startEdit = false;
    },
    closeDuplicate(underWriting) {
      if (!this.closed(underWriting.status_text)) {
        this.$confirm("Are you sure you want to close underwriting?", "", {
          confirmButtonText: "Close",
          cancelButtonText: "Cancel"
        })
          .then(() => {
            const formData = new FormData();
            formData.append("status", STATTII.CLOSED_DUPLICATE);
            this.updatePayment({
              id: underWriting.uuid,
              data: formData
            })
              .then(() => {
                this.$notify({
                  type: "success",
                  title: "Success",
                  message: "Underwriting updated"
                });
              })
              .catch();
          })
          .catch(() => {});
      }
    }
  },
  watch: {
    underWritings: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.list = newVal;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
a {
  transition: $transition-simple-fast;
  &:hover {
    color: $blue-navy;
  }
}
.list {
  background-color: $background-gray;
  margin-top: 3.875rem;
  height: 100%;
  box-sizing: border-box;
  width: 100%;
  position: relative;

  &-header {
    .form-group {
      width: 100%;
      display: inline-block;
      max-width: 600px;
    }
  }

  
  &-table {
    -webkit-transition: width 0.2s ease-in-out;
    -moz-transition: width 0.2s ease-in-out;
    -o-transition: width 0.2s ease-in-out;
    transition: width 0.2s ease-in-out;
    width: 100%;
    padding: $padding-xxl;
    padding-bottom: 5rem;
    position: fixed;
    height: calc(100% - 62px);
    overflow-y: auto;
  }

  &-table-split {
    display: inline-block; 
    width: calc(100% - 500px);
    visibility: visible;
  }

  &-slider {
    display: block;
    background-color: $white;
    width: 500px;
    position: fixed;
    top: 62px;
    bottom: 0;
    right: 0;
    overflow-y: auto;
  }

  .slide-in {
    opacity: 0;
    transform: translate3d(40px, 0, 0);
  }
}

.actions {
  width: 100%;
  text-align: center;
  display: inline-block;
  a {
    display: inline-block;
    width: 25%;
    cursor: pointer;

    svg {
      font-size: 1.5rem;
    }
  }
}
.transition-box {
  margin-bottom: 10px;
  width: 200px;
  height: 100px;
  border-radius: 4px;
  background-color: #409EFF;
  text-align: center;
  color: $white;
  padding: 40px 20px;
  box-sizing: border-box;
  margin-right: 20px;
}
.el-tag {
  text-align: center;
  margin: 0 auto;
  &--warning {
    background-color: $yellow-light !important;
    color: $yellow-dark !important;
  }
  &--success {
    background-color: $green-light !important;
    color: $green-dark !important;
  }
}

.close {
  margin-left: 1rem;
}
a.disabled {
  cursor: not-allowed;
}
</style>